<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import FormTemplatePicker from './components/FormTemplatePicker.vue';

const store = useStore();
const getters = useStoreGetters();
const router = useRouter();
const { t } = useI18n();

const togglingId = ref(null);
const showDeleteConfirmationPopup = ref(false);
const selectedForm = ref({});
const templatePickerRef = ref(null);

const records = computed(() => getters['forms/getForms'].value);
const uiFlags = computed(() => getters['forms/getUIFlags'].value);
const funnels = computed(() => getters['funnels/getFunnels'].value);

const tableHeaders = computed(() => [
  t('FORMS_MGMT.LIST.TABLE_HEADER.NAME'),
  t('FORMS_MGMT.LIST.TABLE_HEADER.STATUS'),
  t('FORMS_MGMT.LIST.TABLE_HEADER.FUNNEL'),
  t('FORMS_MGMT.LIST.TABLE_HEADER.URL'),
  t('FORMS_MGMT.LIST.TABLE_HEADER.ACTIONS'),
]);

const funnelName = funnelId => {
  const funnel = funnels.value.find(item => item.id === funnelId);
  return funnel ? funnel.name : t('FORMS_MGMT.LIST.NO_FUNNEL');
};

const publicLink = form => `${window.location.origin}${form.public_url}`;

const deleteMessage = computed(() => ` ${selectedForm.value.name}?`);

onBeforeMount(() => {
  store.dispatch('forms/get');
  store.dispatch('funnels/get');
});

const goToNewForm = () => {
  templatePickerRef.value?.open();
};

const onTemplateSelected = templateId => {
  router.push({
    name: 'forms_new',
    query: templateId ? { template: templateId } : {},
  });
};

const goToEditForm = form => {
  router.push({ name: 'forms_edit', params: { formId: form.id } });
};

const toggleEnabled = async form => {
  togglingId.value = form.id;
  try {
    await store.dispatch('forms/update', {
      id: form.id,
      enabled: !form.enabled,
    });
    useAlert(t('FORMS_MGMT.TOGGLE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('FORMS_MGMT.TOGGLE.API.ERROR_MESSAGE'));
  } finally {
    togglingId.value = null;
  }
};

const copyLink = async form => {
  try {
    await copyTextToClipboard(publicLink(form));
    useAlert(t('FORMS_MGMT.LINK.COPIED'));
  } catch (error) {
    useAlert(t('FORMS_MGMT.LINK.COPY_ERROR'));
  }
};

const openDeletePopup = form => {
  selectedForm.value = form;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  closeDeletePopup();
  try {
    await store.dispatch('forms/delete', selectedForm.value.id);
    useAlert(t('FORMS_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('FORMS_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('FORMS_MGMT.LOADING')"
    :no-records-found="!uiFlags.isFetching && !records.length"
    :no-records-message="$t('FORMS_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('FORMS_MGMT.HEADER')"
        :description="$t('FORMS_MGMT.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('FORMS_MGMT.HEADER_BTN_TXT')"
            @click="goToNewForm"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="form in records" :key="form.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="mb-1 font-medium break-words text-n-slate-12">
                {{ form.name }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <button
                class="px-2 py-1 text-xs font-medium rounded-full transition-colors"
                :class="
                  form.enabled
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                "
                :disabled="togglingId === form.id"
                @click="toggleEnabled(form)"
              >
                {{
                  form.enabled
                    ? $t('FORMS_MGMT.LIST.STATUS_PUBLISHED')
                    : $t('FORMS_MGMT.LIST.STATUS_DRAFT')
                }}
              </button>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-sm text-n-slate-11">
              {{ funnelName(form.funnel_id) }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <a
                :href="publicLink(form)"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs text-n-blue-text underline"
              >
                {{ publicLink(form) }}
              </a>
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('FORMS_MGMT.LIST.COPY_LINK')"
                  icon="i-lucide-copy"
                  slate
                  xs
                  faded
                  @click="copyLink(form)"
                />
                <Button
                  v-tooltip.top="$t('FORMS_MGMT.LIST.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  @click="goToEditForm(form)"
                />
                <Button
                  v-tooltip.top="$t('FORMS_MGMT.LIST.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDeletePopup(form)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('FORMS_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('FORMS_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('FORMS_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('FORMS_MGMT.DELETE.CONFIRM.NO')"
    />

    <FormTemplatePicker ref="templatePickerRef" @select="onTemplateSelected" />
  </SettingsLayout>
</template>
